<template>
    <div    class="mt-8 relative h-full overflow-y-scroll">
        <div    v-if="viewOnly"
                class="absolute top-0 left-0 z-50 w-full h-full"></div>
        <form method="POST"
              @submit.prevent="submitEvent"
              :action="formRoute"
              ref="alarmProfileForm"
        >
            <input name="_method" type="hidden" :value="id === null ? 'POST' : 'PATCH'">
            <input type="hidden" name="_token" :value="csrf"/>
            <!--
              Tailwind UI : Two Column Cards with separate submit actions
              https://tailwindui.com/components/application-ui/forms/form-layouts#component-db11f83176d113e39bf2559da9344b1c
            -->
            <div>
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span class="text-lg font-medium leading-6 text-gray-900">Alarm Profile</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Give a unique name that will help others identify this alarm profile.
                            </p>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow sm:rounded-md sm:overflow-hidden">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="col-span-6">
                                    <label for="name" class="block text-sm font-medium leading-5 text-gray-700">
                                        Alarm Profile Name</label>
                                    <input type="text" id="name"
                                           name="name"
                                           class="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-200 focus:border-blue-300 sm:text-base sm:leading-5"
                                           v-model="data.name"
                                           required
                                    >
                                </div>
                                <fieldset v-if="showPrivateToggle" class="mt-8 ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Custom Alarm Profile
                                        Mode
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle id="private" name="private" v-model:value="data.private"></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Private Mode</label>
                                                <p class="text-gray-500">Disable private mode to make this alarm profile
                                                    re-usable by other points.</p>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>

                                <fieldset v-if="hasSoundDevices" class="mt-8 ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Sound Device Options
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle name="force_push_indicators"
                                                        id="force_push_indicators"
                                                        v-model:value="data.force_push_indicators"
                                                ></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Push Indicators to
                                                    Points</label>
                                                <p class="text-gray-500">Toggling on will force the value to send to all
                                                    points. Not toggling will send immediately if points are online.</p>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div class="mt-10 sm:mt-0">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span class="text-lg font-medium leading-6 text-gray-900">Enable Alarms</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Thresholds set the boundaries for acceptable point values.
                            </p>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Offline alarms can be generated when a meter is offline for a specific amount of time.
                            </p>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <fieldset class="ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Excursion (Threshold)
                                        Alarms
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle id="threshold_alarms_enabled" name="threshold_alarms_enabled"
                                                        v-model:value="data.threshold_alarms_enabled"></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Excursion (Threshold)
                                                    Alarms</label>
                                                <p class="text-gray-500">Create alarm when point has an excursion
                                                    outside of the thresholds.</p>
                                            </div>
                                        </div>
                                        <div v-if="data.threshold_alarms_enabled" class="mt-4">
                                            <div class="flex items-start">
                                                <div class="flex items-center h-5">
                                                    <toggle id="threshold_resolve_enabled"
                                                            name="threshold_resolve_enabled"
                                                            v-model:value="data.threshold_resolve_enabled"></toggle>
                                                </div>
                                                <div class="ml-3 text-sm leading-5">
                                                    <label class="font-medium text-gray-700">Threshold Automatic
                                                        Resolution</label>
                                                    <p class="text-gray-500">Automatically resolve alarms when points
                                                        are back within the 'acceptable' thresholds.</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="data.threshold_alarms_enabled && data.threshold_resolve_enabled"
                                             class="pl-12 ml-1 mt-2">
                                            <label for="high_alarm_time"
                                                   class="block text-sm font-medium leading-5 text-gray-700">Threshold
                                                Automatic Resolution Buffer Time</label>
                                            <input type="text"
                                                   placeholder="HH:MM"
                                                   name="threshold_resolve_time"
                                                   id="threshold_resolve_time"
                                                   :value="thresholdResolveTimeReadable"
                                                   class="border p-2 text-center rounded-lg w-full"
                                                   :class="instantAlarming ? 'sm:w-1/3' : 'sm:w-1/2'"
                                                   @focus="openModal">
                                        </div>

                                    </div>
                                </fieldset>
                                <fieldset class="mt-4 ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Offline Alarms
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle     id="offline_alarms_enabled"
                                                            v-model:value="data.offline_alarms_enabled"
                                                            name="offline_alarms_enabled"
                                                            @click="updateEnableOfflineAlarmThreshold(data.offline_alarms_enabled)"
                                                ></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Offline Alarms</label>
                                                <p class="text-gray-500">Create alarm when meter goes offline.</p>
                                            </div>
                                        </div>
                                        <div v-if="data.offline_alarms_enabled" class="mt-4">
                                            <div class="flex items-start">
                                                <div class="flex items-center h-5">
                                                    <toggle     id="offline_resolve_enabled"
                                                                v-model:value="data.offline_resolve_enabled"
                                                                name="offline_resolve_enabled">
                                                    ></toggle>
                                                </div>
                                                <div class="ml-3 text-sm leading-5">
                                                    <label class="font-medium text-gray-700">Offline Automatic
                                                        Resolution</label>
                                                    <p class="text-gray-500">Automatically resolve alarms when points
                                                        are back online.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div class="mt-10 sm:mt-0">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span
                                class="text-lg font-medium leading-6 text-gray-900">Alarm Thresholds and Buffer Times</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Help fight alarm fatigue: set a reasonable alarm buffer time to allow normal variations
                                to occur.
                            </p>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                We recommend setting your buffer time in accordance with the sensitivity of the
                                product/area being monitored.
                            </p>
                            <p v-if="instantAlarming" class="mt-1 text-sm leading-5 text-gray-600">
                                Instant Alarming allows you to select points that should alarm immediately for <b>safety-critical</b>
                                reasons.
                                If this isn't a safety-critical system, consider using a reasonable alarm buffer time
                                instead.
                            </p>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="grid grid-cols-6 gap-6">
                                    <span
                                        class="col-span-6 text-base leading-6 font-medium text-gray-900 border-b border-gray-200">Excursion (Threshold) Alarms</span>

                                    <template
                                        v-for="[valueKey, timeKey, valueText, valueInstantKey] in alarmThresholdVariables">
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div class="w-full flex justify-between -mb-1">
                                                <label class="w-2/3 text-sm font-medium leading-5 text-gray-700 mb-3">
                                                    {{ valueText }}<br/>
                                                    Threshold Limit
                                                </label>
                                                <small-arrow :red="valueKey.includes('alarm')"
                                                             :direction-up="valueKey.includes('high')"></small-arrow>
                                            </div>
                                            <input type="text" :id="valueKey"
                                                   :name="valueKey"
                                                   :value="data[valueKey]"
                                                   @input="debounceInput"
                                                   placeholder="Optional"
                                                   class="mt-1 block w-full py-2 px-3 border border-gray-300 text-center rounded-md shadow-sm focus:outline-none focus:ring-blue-200 focus:border-blue-300 sm:text-base sm:leading-5">
                                        </div>
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div v-if="data.threshold_alarms_enabled">
                                                <label class="block text-sm font-medium leading-5 text-gray-700 mb-3">
                                                    {{ valueText }}<br/>
                                                    Threshold Buffer Time
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="HH:MM"
                                                    :name="timeKey"
                                                    :id="timeKey"
                                                    :disabled="data[valueInstantKey]"
                                                    :value="data[valueInstantKey] ? 'Instant' : readable[valueKey]"
                                                    class="border p-2 w-full text-center rounded-lg"
                                                    @focus="openModal">
                                            </div>
                                        </div>
                                        <div v-if="instantAlarming"
                                             :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div v-if="data.threshold_alarms_enabled">
                                                <label class="block text-sm font-medium leading-5 text-gray-700 mb-3">
                                                    {{ valueText }}<br/>
                                                    Instant Alarming</label>
                                                <toggle name="high_alarm_instant"
                                                        id="high_alarm_instant"
                                                        v-model:value="data[valueInstantKey]"
                                                ></toggle>
                                            </div>
                                        </div>

                                    </template>
                                    <template v-if="data.offline_alarms_enabled">
                                        <span
                                            class="col-span-6 text-base leading-6 font-medium text-gray-900 border-b border-gray-200">Offline Alarms</span>
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <label class="block text-sm font-medium leading-5 text-gray-700">Meter not
                                                transmitting data</label>
                                            <label  class="block text-sm font-medium leading-5 text-gray-700 mt-2">
                                                We recommend a buffer time of at least 01:35 (1 hour & 35 minutes).
                                            </label>
                                        </div>
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div v-if="data.offline_alarms_enabled">
                                                <label for="offline_alarm_time"
                                                       class="block text-sm font-medium leading-5 text-gray-700">Offline
                                                    Alarm Buffer Time</label>
                                                <input type="text"
                                                       placeholder="HH:MM"
                                                       name="offline_alarm_time"
                                                       id="offline_alarm_time"
                                                       :value="offlineAlarmTimeReadable"
                                                       class="border p-2 w-full text-center rounded-lg"
                                                       @focus="openModal">
                                            </div>
                                        </div>
                                    </template>


                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div    v-if="showAlarmHandler"
                    class="mt-10 sm:mt-0">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="flex flex-col px-4 sm:px-0 h-full">
                            <span  class="text-lg font-medium leading-6 text-gray-900">
                                Alarm Handler
                            </span>
                            <textarea class="mt-1 text-sm leading-5 text-gray-600 border-0 grow bg-white"
                                        :value="data.alarmHandlerDescriptions[data.handler.handler]"
                                        disabled>
                            </textarea>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <alarm-handler-select   v-model="data['handler']"
                                                        :handlers="data.alarmHandlers"
                                ></alarm-handler-select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div    v-if="showAlarmHandler"
                    class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div class="mt-10 sm:mt-0">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span
                                class="text-lg font-medium leading-6 text-gray-900">Alarm Thresholds Visualization</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Get a feel for how your thresholds are set with this handy chart!
                            </p>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <alarm-profiles-graph
                                    class=""
                                    v-if="!loading && thresholdsAreNumbers"
                                    :high_alarm_value="data.high_alarm_value"
                                    :high_alarm_instant="data.high_alarm_instant"
                                    :high_alarm_time="highAlarmTimeReadable"
                                    :high_caution_value="data.high_caution_value"
                                    :high_caution_instant="data.high_caution_instant"
                                    :high_caution_time="highCautionTimeReadable"
                                    :low_caution_value="data.low_caution_value"
                                    :low_caution_instant="data.low_caution_instant"
                                    :low_caution_time="lowCautionTimeReadable"
                                    :low_alarm_value="data.low_alarm_value"
                                    :low_alarm_instant="data.low_alarm_instant"
                                    :low_alarm_time="lowAlarmTimeReadable"
                                >

                                </alarm-profiles-graph>
                                <div v-else-if="!thresholdsAreNumbers" class="py-20">
                  <span class="text-center text-base text-gray-500">
                    All Threshold Limits must be Numbers or Blank.  Please check them.
                  </span>
                                </div>
                                <div v-else class="my-20">
                  <span class="text-center text-base text-gray-500">
                    Creating Graph ... Please Wait
                  </span>
                                </div>
                                <div v-if="errors.length > 0">
                                    <error-message>
                                        Errors were encountered while saving this alarm profile:
                                        <li v-for="error in errors">{{ error }}</li>
                                    </error-message>
                                </div>
                            </div>
                            <div v-if="!disableSubmit && this.permissions.can('edit_alarm_profiles') && !viewOnly"
                                 class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                              <confirmation-modal @strict-cfr-disabled="submitEvent" @authentication-success="submitEvent">
                                <template v-slot:submitAction="{ onSubmit }">
                                  <button @click.prevent="onSubmit"
                                      class="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:bg-blue-500 active:bg-blue-600">
                                    Save Alarm Profile
                                  </button>
                                </template>
                              </confirmation-modal>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hour-minute-picker
                v-model:is-open="showModal"
                :seconds="modalSeconds"
                @datePicked="listenForDatepicker"></hour-minute-picker>
        </form>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import {DateTime} from 'luxon';

import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import DateTimePicker from "@/components/Reports/DateTimePicker";
import Loading from "@/components/elements/loading";
import AlarmProfilesGraph from "@/components/alarmProfiles/alarmProfilesGraph";
import HourMinutePicker from "@/components/elements/hourMinutePicker";
import ErrorMessage from "@/components/ErrorMessage";
import SmallArrow from "@/components/elements/smallArrow";
import Toggle from "@/components/elements/toggle"
import AlarmHandlerSelect from "@/components/alarmProfiles/AlarmHandlerSelect";
import FloatingInput from "@/components/elements/FloatingInput";

export default {
    name: "alarmProfilesEdit",
    mixins: [SonicuCoreMixin],
    components: {
        FloatingInput,
        AlarmHandlerSelect,
        SmallArrow,
        Toggle,
        ErrorMessage,
        HourMinutePicker,
        AlarmProfilesGraph,
        Loading,
        DateTimePicker, DateTime
    },
    props: {
        id: {
            type: Number,
        },
        instantAlarmingProp: {
            type: Boolean,
            default: false,
        },
        disableSubmit: {
            type: Boolean,
            default: false,
        },
        isAjax: {
            type: Boolean,
            default: false,
        },
        privateMode: {
            type: Boolean,
            default: false,
        },
        defaultName: {
            type: String,
            default: null,
        },
        viewOnly: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
    },
    data() {
        return {
            data: {
                force_push_indicators: false,
                threshold_alarms_enabled: false,
            },
            showModal: false,
            showModalEvent: null,
            modalSeconds: null,
            loading: true,
            instantAlarming: this.instantAlarmingProp,
            errors: [],
            showPrivateToggle: this.privateMode,
            alarmThresholdVariables: [
                ['high_alarm_value', 'high_alarm_time', 'High Alarm', 'high_alarm_instant'],
                ['high_caution_value', 'high_caution_time', 'High Caution', 'high_caution_instant'],
                ['low_caution_value', 'low_caution_time', 'Low Caution', 'low_caution_instant'],
                ['low_alarm_value', 'low_alarm_time', 'Low Alarm', 'low_alarm_instant'],
            ],
            handler: {},
            alarmHandlers: {},
        }
    },
    created() {
        this.getData();
    },
    computed: {
        readable() {
            return {
                'high_alarm_value': this.highAlarmTimeReadable,
                'high_caution_value': this.highCautionTimeReadable,
                'low_caution_value': this.lowCautionTimeReadable,
                'low_alarm_value': this.lowAlarmTimeReadable,
            }
        },
        hasSoundDevices() {
            if (!('has_sound_devices' in this.data)) {
                return false
            }
            return this.data.has_sound_devices
        },
        highAlarmTimeReadable() {
            if (_.isNil(this.data.high_alarm_time)) {
                return '';
            }
            let minutes = this.data.high_alarm_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        highCautionTimeReadable() {
            if (_.isNil(this.data.high_caution_time)) {
                return '';
            }
            let minutes = this.data.high_caution_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        lowCautionTimeReadable() {
            if (_.isNil(this.data.low_caution_time)) {
                return '';
            }
            let minutes = this.data.low_caution_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        lowAlarmTimeReadable() {
            if (_.isNil(this.data.low_alarm_time)) {
                return '';
            }
            let minutes = this.data.low_alarm_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        offlineAlarmTimeReadable() {
            if (_.isNil(this.data.offline_alarm_time)) {
                return '01:35';
            }
            let minutes = this.data.offline_alarm_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        thresholdResolveTimeReadable() {
            if (_.isNil(this.data.threshold_resolve_time)) {
                return '';
            }
            let minutes = this.data.threshold_resolve_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        offlineTimeValid() {
            // If offline is not set then consider it valid
            if (_.isNil(this.data.offline_alarm_time)) {
                return true
            }
            // if it is a sonicu user, also hop out
            if (this.permissions.isSonicu()) {
                return true
            }
            // Make sure that the offline time is at least 35 minutes.
            return this.data.offline_alarm_time / 60 >= 35
        },
        thresholdsAreNumbers() {
            return this.validateThresholdIsNullBlankOrNumber(this.data.high_alarm_value) &&
                this.validateThresholdIsNullBlankOrNumber(this.data.high_caution_value) &&
                this.validateThresholdIsNullBlankOrNumber(this.data.low_caution_value) &&
                this.validateThresholdIsNullBlankOrNumber(this.data.low_alarm_value)
        },
        formRoute() {
            if (_.isNil(this.id)) {
                return this.company.buildCompanyApiInternalRoute('alarm-profiles/');
            } else {
                return this.company.buildCompanyApiInternalRoute('alarm-profiles/' + this.id);
            }
        },
        formMethod() {
            if (_.isNil(this.id)) {
                return 'post';
            } else {
                return 'patch';
            }
        },
        showAlarmHandler() {
            return this.data.alarmHandlers && Object.keys(this.data.alarmHandlers).length > 1
        },
    },
    methods: {
        getData() {
            let route = this.id === null
                ? this.company.buildCompanyApiInternalRoute('alarm-profiles/create')
                : this.company.buildCompanyApiInternalRoute('alarm-profiles/' + this.id);
            axios.get(route).then(
                response => {
                    this.data = response.data;
                    if (this.privateMode) {
                        this.data.private = true;
                    }
                    if (this.data.private) {
                        this.showPrivateToggle = true;
                    }
                    if (this.defaultName) {
                        this.data.name = this.defaultName;
                    }

                    this.data.handler = this.data.alarm_handler_data

                    // Set the modalSeconds based on a default time if offline alarm time is not set.

                    this.loading = false;
                }
            ).catch(
                error => {

                }
            )
        },
        debounceInput: _.debounce(function (e) {
            this.data[e.srcElement.id] = e.target.value;
        }, 500),
        openModal(e) {
            this.showModalEvent = e;

            // Get modalSeconds from the input.  Defaulting to values based on key
            let elementId = e.srcElement.id
            if (elementId === 'offline_alarm_time') {
                this.modalSeconds = _.isNil(this.data.offline_alarm_time)
                    ? "" + 60 * 95 + ""
                    : this.data.offline_alarm_time
            }
            else {
                this.modalSeconds = this.data[e.srcElement.id]
            }

            // Parse the string seconds into an integer
            this.modalSeconds = parseInt(this.modalSeconds);
            this.showModal = true;
        },
        listenForDatepicker(val) {
            // We are going to have an exception for offline alarm times, they if reset, should go to 95 minutes.
            if (this.showModalEvent.srcElement.id === 'offline_alarm_time' && _.isNil(val)) {
                this.data.offline_alarm_time = 60 * 95
            } else {
                // All other values should be set to the value of the modal.
                this.data[this.showModalEvent.srcElement.id] = val;
            }

            // Hide the modal
            this.showModalEvent = null;
        },
        submitEvent(confirmationRequestUuid = null) {
            this.errors = []
            // Cancel the save if the offline time is less than 35 minutes.
            // TODO consider having this set the offline time to 35 minutes for the user to hit save again.
            if (this.data.offline_alarms_enabled && !this.offlineTimeValid) {
                this.errors = ['Offline time must be at least 35 minutes.']
                return
            }

            if (!this.validateThresholdValues()) {
                return
            }
            console.log(this.data)
            this.data['confirmation_request_uuid'] = confirmationRequestUuid;
            axios({'method': this.formMethod, 'url': this.formRoute, 'data': this.data}).then(
                response => {
                    if (this.isAjax) {  // Emit that it was saved
                        this.$emit('profileSaved', response);
                    } else {
                        window.location.href = this.company.buildCompanyRoute('alarm-profiles');
                    }
                }
            ).catch(
                error => {
                    this.errors = error.response.data.errors;
                }
            )
        },
        validateThresholdValues() {
            if (! this.validateThresholdsAreNumbers()) {
                this.errors.push('Some Thresholds are not Numbers')
                return false
            }

            let thresholdRankings = {
                high_alarm_value: 4,
                high_caution_value: 3,
                low_caution_value: 2,
                low_alarm_value: 1,
            }
            let valid = true


            Object.keys(thresholdRankings).forEach(highKey => {
                let high = this.data[highKey]
                if (_.isNil(high)) {
                    return
                }
                Object.keys(thresholdRankings).forEach(compKey => {
                    if (!_.isNil(this.data[compKey]) && thresholdRankings[compKey] < thresholdRankings[highKey]) {
                        if (this.data[compKey] > high) {
                            valid = false
                            this.errors.push(`${compKey} value cannot be higher than ${highKey}`)
                        }
                    }
                })
            })

            return valid
        },
        validateThresholdsAreNumbers() {
            let allNumbers = true
            Object.keys(this.readable).forEach(key => {
                if (_.isNil(this.data[key]) || this.data[key] === '') {
                    this.data[key] = null
                    return
                }
                let tmp = parseFloat(`${this.data[key]}`)
                if (_.isNaN(tmp)) {
                    allNumbers = false
                    return
                }
                this.data[key] = tmp
            })
            return allNumbers
        },
        validateThresholdIsNullBlankOrNumber(threshold) {
            if (_.isNil(threshold) || threshold === '') {
                // We are good because it is blank or null
                return true
            }

            // Test if the threshold can be converted to a float.
            let tmp = parseFloat(`${threshold}`)

            // If the value is not a number, then it is not valid. (isNumber causes recursion)
            return ! _.isNaN(tmp)
        },
        updateEnableOfflineAlarmThreshold(enabled) {
            // This is to ensure that the offline alarm time is set to a sensable default
            // If the offline alarm is being enabled & the offline alarm time is not set, set it to 95 minutes.
            if (enabled && _.isNil(this.data.offline_alarm_time)) {
                this.data.offline_alarm_time = 60 * 95
            }
        },
    },
    watch: {
        id: function (val) {
            this.getData();
        }
    }
}
</script>

<style scoped>

</style>